import type { SalesManager } from '@/features/accounts/api';

/**
 * Resolve the Account Manager value to seed into the quote form when a customer is
 * picked on the ERP > Quotes "New Quote" flow.
 *
 * The account's stored AM id (from the `/v4/accounts` list — a PG `users.id` or, via the
 * legacy COALESCE fallback, a `mongo_user_id`) is matched against the loaded sales-manager
 * options and normalised to the dropdown's value space (`mongoUserId || id`, see
 * QuoteFormModal's `<SelectItem value={m.mongoUserId || m.id}>`), so the option renders as
 * selected. When the AM isn't in the loaded list (not yet fetched, or a former/non-staff
 * manager) the raw account id is returned — still non-empty, so the required-field guard
 * passes exactly like the account-page flow. Returns '' when the account has no AM.
 *
 * This mirrors how the account-page "New Quote" pre-fills the AM via
 * `QuotesTab` `quoteCreatePrefill.salesManagerId`; the ERP page can't pre-fill it at
 * modal-open (the customer is chosen inside the modal), so it must seed on customer-select.
 */
export function resolveAccountManagerSeed(
  accountSalesManagerId: string | null | undefined,
  salesManagers: Pick<SalesManager, 'id' | 'mongoUserId'>[],
  fallbackManagerId?: string | null,
): string {
  const managerId = accountSalesManagerId || fallbackManagerId;
  if (!managerId) return '';
  const match = salesManagers.find((m) => m.id === managerId || m.mongoUserId === managerId);
  if (match) return match.mongoUserId || match.id;
  return managerId;
}
